import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { logger } from '@magnetar/utils'
import { useAuth } from '@/hooks/useAuth'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { BroadcastDevice } from '@magnetar/data-access'
import { broadcastDeviceService } from '@/services/broadcast_device_service'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAllExaminationRooms } from '@/hooks/useAllExaminationRooms'
import { Check, X } from 'lucide-react'
import IfAnyPermission from '@/components/IfAnyPermission'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { AllowedAction } from '@/data/allowedActions'

// 基本資料的編輯權限 — 對齊後端 PUT /api/broadcast-device/:device_id
// 的 checkAdminOrProviderClinicAccess（admin + provider only）。
// clinic / clinic_group user 看得到目前的設定值但不能修改。
const BROADCAST_DEVICE_INFO_EDIT_PERMISSIONS = [
  { module: 'system_clinic', action: AllowedAction.U },
  { module: 'system_provider', action: AllowedAction.U },
  { module: 'provider_clinic_management', action: AllowedAction.U },
]

type FormValues = {
  broadcast_type: 'tv' | 'clinic_vod' | 'health_vod'
  is_active: boolean
  examination_room_ids?: number[]
  name?: string
}

// 擴展 BroadcastDevice 類型以包含 examination_room_ids
type ExtendedDevice = BroadcastDevice & {
  examination_room_ids?: number[]
}

type InfoSettingProps = {
  device?: ExtendedDevice
  isLoading?: boolean
  onSuccess?: () => void
}

export function InfoSetting({ device, isLoading, onSuccess }: InfoSettingProps) {
  const { token } = useAuth()
  const { hasPermission } = useMyPermissions()
  const canEditInfo = BROADCAST_DEVICE_INFO_EDIT_PERMISSIONS.some((p) =>
    hasPermission(p.module, p.action),
  )
  const [selectedRooms, setSelectedRooms] = useState<number[]>([])
  const form = useForm<FormValues>({
    defaultValues: {
      broadcast_type: 'tv',
      is_active: true,
      examination_room_ids: [],
      name: '',
    },
  })

  // Get clinic ID from device
  const clinicId = device?.clinic_id

  // Fetch all examination rooms for the clinic
  const { examinationRooms, isLoading: isLoadingRooms } = useAllExaminationRooms(clinicId)

  // Initialize selected rooms from API data
  useEffect(() => {
    logger.log('device', device)
    if (device && device.examination_room_ids && device.examination_room_ids.length > 0) {
      // 從設備數據中獲取診間 ID
      logger.log('從 API 獲取的診間 IDs:', device.examination_room_ids)
      setSelectedRooms(device.examination_room_ids)
    } else {
      logger.log('沒有找到診間關聯或設備數據')
      setSelectedRooms([])
    }
  }, [device])

  // Update form values when device data is loaded
  useEffect(() => {
    if (device) {
      form.reset({
        broadcast_type: device.broadcast_type,
        is_active: device.is_active,
        examination_room_ids: selectedRooms,
        name: device.name || '',
      })
    }
  }, [device, form, selectedRooms])

  const onSubmit = async (values: FormValues) => {
    if (!device || !token) return

    try {
      const response = await broadcastDeviceService.updateBroadcastDevice(
        device.broadcast_device_id,
        {
          broadcast_type: values.broadcast_type,
          is_active: values.is_active,
          examination_room_ids: values.examination_room_ids || [],
          name: values.name,
        },
        token,
      )

      if (!response.success) {
        throw new Error(response.error?.message || '更新廣播設備失敗')
      }

      toast.success('更新廣播設備成功')

      onSuccess?.()
    } catch {
      toast.error('更新廣播設備失敗')
    }
  }

  if (isLoading) {
    return <div>載入中...</div>
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold tracking-tight">編輯基本資料</h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>設備名稱</FormLabel>
                <FormControl>
                  <Input placeholder="請輸入設備名稱" disabled={!canEditInfo} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="broadcast_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>播放類型</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={!canEditInfo}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="請選擇播放類型" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="tv">電視</SelectItem>
                    <SelectItem value="clinic_vod">診所影片</SelectItem>
                    <SelectItem value="health_vod">衛教影片</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <IfAnyPermission permissions={BROADCAST_DEVICE_INFO_EDIT_PERMISSIONS}>
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">狀態</FormLabel>
                    <FormDescription>{field.value ? '啟用' : '停用'}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </IfAnyPermission>

          <div className="space-y-4">
            <FormLabel>綁定診間</FormLabel>
            <FormDescription>選擇要綁定到此廣播設備的診間</FormDescription>

            <FormField
              control={form.control}
              name="examination_room_ids"
              render={({ field }) => (
                <FormItem>
                  <ScrollArea className="h-[200px] rounded-md border p-4">
                    <div className="space-y-2">
                      {isLoadingRooms ? (
                        <div>載入診間中...</div>
                      ) : examinationRooms.length === 0 ? (
                        <div>無可用診間</div>
                      ) : (
                        examinationRooms.map((room) => {
                          const isSelected = selectedRooms.includes(room.examination_room_id)
                          return (
                            <div
                              key={room.examination_room_id}
                              className={`flex items-center justify-between rounded-md border p-3 ${
                                isSelected ? 'bg-primary/10 border-primary' : ''
                              } ${canEditInfo ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
                              onClick={() => {
                                if (!canEditInfo) return
                                const newSelectedRooms = isSelected
                                  ? selectedRooms.filter((id) => id !== room.examination_room_id)
                                  : [...selectedRooms, room.examination_room_id]

                                setSelectedRooms(newSelectedRooms)
                                field.onChange(newSelectedRooms)
                              }}
                            >
                              <div className="font-medium">{room.name}</div>
                              {isSelected ? (
                                <Check className="h-5 w-5 text-primary" />
                              ) : (
                                <X className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                          )
                        })
                      )}
                    </div>
                  </ScrollArea>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <IfAnyPermission permissions={BROADCAST_DEVICE_INFO_EDIT_PERMISSIONS}>
            <Button type="submit">儲存</Button>
          </IfAnyPermission>
        </form>
      </Form>
    </div>
  )
}
